import { useEffect, useRef, useMemo, useCallback, lazy } from 'react'
import { oneOfType, node, object, bool, func, string } from 'prop-types'
import { useInView } from 'react-intersection-observer'
import { css } from '@emotion/react'
import useMedia from 'use-media'
import { skipToken } from '@reduxjs/toolkit/query'

import { useFetchAppScopesQuery } from '@/apis/accounts/slice'
import styles from '@/layout/category-styles'
import mediaQueries from '@/layout/media-queries'
import { handleChatWindow } from '@/utils/chat'
import { canUseWindow } from '@/utils/browser'
import { AMPLITUDE_EVENTS, PRODUCT_PAGE_TABS } from '@/constants'
import { fetchFeedbackSummary } from '@/store/feedback'
import { fetchResources } from '@/store/resources'
import {
  getAppResources,
  isAppInstalled,
  getProductFeedbackSummary,
} from '@/store/selectors'
import { logAmplitudeEvent } from '@/store/stats'
import { filterForDevsOtherProducts, isTutorialApp } from '@/utils/products'
import { useTypedDispatch, useTypedSelector } from '@/store/hooks'

import { SuspenseWrapper } from '@/components/Suspense/SuspenseWrapper'
import Sidebar from '@/components/Sidebar'
import ActionButton from './Action'
import Description from './Description'
import TextSection from './TextSection'
import Support from './Support'
import Upgrades from './Upgrades'
import Header from './Header/Header'
import AdditionalInfo from './Header/AdditionalInfo'
import MoreByDev from './MoreByDev'
import Changelog from './Changelog'
import AppTerms from './AppTerms'
import useParamSelector from '@/hooks/useParamSelector'

const Reviews = lazy(() => import('./Reviews'))

const wrapperCss = css`
  width: 100%;
`

const Product = ({
  product,
  feedback,
  hideSidebar,
  className,
  sidebarComponent: SidebarComponent = Sidebar,
  developer,
}) => {
  const {
    id,
    name,
    features,
    benefits,
    longDescription,
    logo,
    icons,
    images,
    shortDescription,
    payment,
    screenshots,
    customProps,
    elements,
    changelog,
    privacyPolicyUrl,
    termsAndConditionsUrl,
    authorization,
    underMaintenance,
    hasInAppUpgrades,
  } = product

  const dispatch = useTypedDispatch()

  const canVote = useParamSelector(isAppInstalled, id)
  const resources = useParamSelector(getAppResources, id)
  const feedbackSummary = useTypedSelector(getProductFeedbackSummary)

  const hideChatWindow = useMedia({ maxWidth: mediaQueries.SMALL })

  useEffect(() => {
    handleChatWindow(hideChatWindow)
  }, [hideChatWindow])

  useEffect(() => {
    dispatch(fetchFeedbackSummary.pending({ id }))
  }, [dispatch, id])

  useEffect(() => {
    if (hasInAppUpgrades) {
      dispatch(fetchResources.pending({ id }))
    }
  }, [dispatch, hasInAppUpgrades, id])

  const { rating, reviews, ratesHistogram } = useMemo(
    () => ({
      rating: {
        id,
        rate: feedbackSummary?.rate || feedback?.rate,
        votes: feedbackSummary?.votes || feedback?.votes,
      },
      reviews: feedbackSummary?.reviews || feedback?.reviews,
      ratesHistogram: feedbackSummary?.histogram || feedback?.histogram,
    }),
    [feedback, feedbackSummary, id]
  )

  const isTutorialApplication = isTutorialApp(product)
  const isService = customProps?.isService === 'true'
  const isVerifiedApp = customProps?.verified === 'true'

  const productClientId = authorization?.clientId
  const { data: client } = useFetchAppScopesQuery(
    productClientId ?? skipToken,
    { skip: !productClientId }
  )
  const productScopes = client?.scopes ?? []

  useEffect(() => {
    const appParamsExist = !!id && !!name

    if (appParamsExist) {
      dispatch(
        logAmplitudeEvent({
          name: AMPLITUDE_EVENTS.PAGEVIEW,
          params: {
            appId: id,
            appName: name,
          },
        })
      )
    }
  }, [dispatch, id, name])

  const widgets = elements?.widgets || []
  const galleryImages = useMemo(
    () =>
      screenshots?.map((screenshot, index) => ({
        localImage: images?.[index],
        ...screenshot,
      })),
    [images, screenshots]
  )

  const headerRef = useRef(null)
  let topMargin = 0

  if (headerRef.current) {
    const { height } = headerRef.current.getBoundingClientRect()
    topMargin = 90 + height
  }

  const [upgradesRef, inUpgradesView] = useInView({
    rootMargin: `-${topMargin}px 0px 0px 0px`,
    ...(canUseWindow && { root: window.document }),
  })

  const [descriptionRef, inDescriptionView] = useInView({
    rootMargin: `-${topMargin}px 0px 0px 0px`,
    ...(canUseWindow && { root: window.document }),
  })

  const [featuresRef, inFeaturesView] = useInView({
    rootMargin: `-${topMargin}px 0px 0px 0px`,
    ...(canUseWindow && { root: window.document }),
  })

  const [benefitsRef, inBenefitsView] = useInView({
    rootMargin: `-${topMargin}px 0px 0px 0px`,
    ...(canUseWindow && { root: window.document }),
  })

  const [changelogRef, inChangelogView] = useInView({
    rootMargin: `-${topMargin}px 0px 0px 0px`,
    ...(canUseWindow && { root: window.document }),
  })

  const [appTermsRef, inAppTermsView] = useInView({
    rootMargin: `-${topMargin}px 0px 0px 0px`,
    ...(canUseWindow && { root: window.document }),
  })

  const [reviewsRef, inReviewsView] = useInView({
    rootMargin: `-${topMargin}px 0px 0px 0px`,
    ...(canUseWindow && { root: window.document }),
  })

  const [supportRef, inSupportView] = useInView({
    rootMargin: `-${topMargin}px 0px 0px 0px`,
    ...(canUseWindow && { root: window.document }),
  })

  const activeTab = useMemo(() => {
    if (inDescriptionView) {
      return PRODUCT_PAGE_TABS.DESCRIPTION
    } else if (inUpgradesView) {
      return PRODUCT_PAGE_TABS.UPGRADES
    } else if (inFeaturesView) {
      return PRODUCT_PAGE_TABS.FEATURES
    } else if (inBenefitsView) {
      return PRODUCT_PAGE_TABS.BENEFITS
    } else if (inChangelogView) {
      return PRODUCT_PAGE_TABS.CHANGELOG
    } else if (inAppTermsView) {
      return PRODUCT_PAGE_TABS.APP_TERMS
    } else if (inReviewsView) {
      return PRODUCT_PAGE_TABS.REVIEWS
    } else if (inSupportView) {
      return PRODUCT_PAGE_TABS.SUPPORT
    } else {
      return null
    }
  }, [
    inDescriptionView,
    inUpgradesView,
    inFeaturesView,
    inBenefitsView,
    inChangelogView,
    inReviewsView,
    inAppTermsView,
    inSupportView,
  ])

  const tabs = useMemo(() => {
    const hasDescription = !!longDescription || screenshots?.length > 0
    const hasUpgrades = resources.length > 0
    const hasFeatures = !!features
    const hasBenefits = !!benefits
    const hasChangelog = !!changelog
    const hasReviews = !isTutorialApplication
    const hasSupport = !isTutorialApplication

    return [
      { id: PRODUCT_PAGE_TABS.DESCRIPTION, title: 'Overview' },
      { id: PRODUCT_PAGE_TABS.UPGRADES, title: 'In-App Upgrades' },
      { id: PRODUCT_PAGE_TABS.FEATURES, title: 'Key Features' },
      { id: PRODUCT_PAGE_TABS.BENEFITS, title: 'Benefits' },
      { id: PRODUCT_PAGE_TABS.CHANGELOG, title: "What's new" },
      { id: PRODUCT_PAGE_TABS.APP_TERMS, title: 'App Terms' },
      { id: PRODUCT_PAGE_TABS.REVIEWS, title: 'Reviews' },
      { id: PRODUCT_PAGE_TABS.SUPPORT, title: 'Support' },
    ].filter((tab) => {
      if (tab.id === PRODUCT_PAGE_TABS.DESCRIPTION && !hasDescription) {
        return false
      } else if (tab.id === PRODUCT_PAGE_TABS.UPGRADES && !hasUpgrades) {
        return false
      } else if (tab.id === PRODUCT_PAGE_TABS.FEATURES && !hasFeatures) {
        return false
      } else if (tab.id === PRODUCT_PAGE_TABS.BENEFITS && !hasBenefits) {
        return false
      } else if (tab.id === PRODUCT_PAGE_TABS.CHANGELOG && !hasChangelog) {
        return false
      } else if (tab.id === PRODUCT_PAGE_TABS.REVIEWS && !hasReviews) {
        return false
      } else if (tab.id === PRODUCT_PAGE_TABS.SUPPORT && !hasSupport) {
        return false
      }

      return true
    })
  }, [
    benefits,
    features,
    resources,
    longDescription,
    screenshots,
    changelog,
    isTutorialApplication,
  ])

  const devsOtherProducts = useMemo(
    () =>
      developer
        ? filterForDevsOtherProducts(
            [...developer?.apps, ...developer?.services],
            developer?.id,
            id
          )
        : [],
    [developer, id]
  )

  const additionalInfo = useCallback(
    (isCompact) => (
      <AdditionalInfo
        product={product}
        rating={rating}
        isTutorialApplication={isTutorialApplication}
        isService={isService}
        developer={developer}
        isCompact={isCompact}
      />
    ),
    [product, rating, isTutorialApplication, isService, developer]
  )

  return (
    <article css={styles.category}>
      <div className="content">
        {!hideSidebar && <SidebarComponent className="sidebar" />}

        <div css={wrapperCss} className={`body ${className || ''}`}>
          <Header
            ref={headerRef}
            name={name}
            icons={icons}
            logo={logo}
            description={shortDescription}
            dataTest="product"
            activeTab={activeTab}
            tabs={tabs}
            payment={payment}
            actionBtn={underMaintenance ? null : <ActionButton id={id} />}
            additionalInfo={additionalInfo}
            underMaintenance={underMaintenance}
          />

          <Description
            widgets={widgets}
            description={longDescription}
            screenshots={galleryImages}
            videoUrl={customProps?.appVideoUrl || null}
            ref={descriptionRef}
            id={id}
          />

          {resources?.length > 0 && (
            <Upgrades items={resources} ref={upgradesRef} />
          )}

          {features?.length > 0 && (
            <TextSection
              items={features}
              id={PRODUCT_PAGE_TABS.FEATURES}
              title="Key Features"
              ref={featuresRef}
              cols={2}
              color="#ffd000"
            />
          )}

          {benefits?.length > 0 && (
            <TextSection
              items={benefits}
              id={PRODUCT_PAGE_TABS.BENEFITS}
              title="Benefits"
              ref={benefitsRef}
              cols={3}
              color="#69d598"
            />
          )}

          <Changelog ref={changelogRef} changelog={changelog} />

          <AppTerms
            ref={appTermsRef}
            name={name}
            privacyPolicyUrl={privacyPolicyUrl}
            termsAndConditionsUrl={termsAndConditionsUrl}
            scopes={productScopes}
          />

          {!isTutorialApplication && (
            <SuspenseWrapper>
              <Reviews
                id={id}
                ref={reviewsRef}
                rate={rating?.rate}
                votes={rating?.votes}
                histogram={ratesHistogram}
                reviews={reviews}
                canVote={canVote}
                developer={developer}
              />
            </SuspenseWrapper>
          )}

          {!isTutorialApplication && (
            <Support
              product={product}
              developer={developer}
              isVerifiedApp={isVerifiedApp}
              ref={supportRef}
            />
          )}

          {!!devsOtherProducts?.length && (
            <MoreByDev
              products={devsOtherProducts}
              heading={
                isTutorialApplication
                  ? 'Explore more apps'
                  : 'More by this developer'
              }
            />
          )}
        </div>
      </div>
    </article>
  )
}

Product.propTypes = {
  className: string,
  product: object,
  feedback: object,
  hideSidebar: bool,
  sidebarComponent: oneOfType([node, func, object]),
  developer: object,
}

export default Product
